import { useRef, useState } from "react";
import { X, Plus, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const MAX_TOTAL_ATTACH_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Caja "Mensaje al cliente" — textarea libre + selector de fotos + botón
 * Enviar. Llama a `POST /api/repairs/{id}/customer-message` y deja registro
 * en el timeline (eso lo maneja el backend).
 *
 * Props:
 *  - isNew: bool — si true, todo está deshabilitado (reparación aún no creada).
 *  - repairId: id de la reparación.
 *  - customer: objeto cliente; se usa `customer.email` como destino.
 *  - onSent: callback con la reparación actualizada tras enviar.
 */
export default function CustomerMessageBox({ isNew, repairId, customer, onSent }) {
  const [text, setText] = useState("");
  // [{ id, name, content_type, data_b64, size }]
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const totalBytes = attachments.reduce((s, a) => s + (a.size || 0), 0);
  const customerEmail = (customer?.email || "").trim();
  const canSend =
    !isNew && !!repairId && !!customerEmail && !!text.trim() && totalBytes <= MAX_TOTAL_ATTACH_BYTES;

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const next = [...attachments];
    for (const f of files) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name}: sólo se permiten imágenes`);
        continue;
      }
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result || "").split(",")[1] || "");
        r.onerror = reject;
        r.readAsDataURL(f);
      });
      next.push({
        // ID estable por archivo (evita usar el índice como key)
        id: `${f.name}-${f.size}-${f.lastModified || Date.now()}-${next.length}`,
        name: f.name,
        content_type: f.type || "image/jpeg",
        data_b64: b64,
        size: f.size,
      });
    }
    setAttachments(next);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeAttachment = (id) => {
    setAttachments(attachments.filter((a) => a.id !== id));
  };

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const payload = {
        text: text.trim(),
        attachments: attachments.map(({ name, content_type, data_b64 }) => ({
          name, content_type, data_b64,
        })),
      };
      const r = await api.post(`/repairs/${repairId}/customer-message`, payload);
      toast.success(`Mensaje enviado a ${customerEmail}`);
      setText("");
      setAttachments([]);
      onSent?.(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-4 mt-2 border-t border-[#E5E5E0]" data-testid="customer-message-box">
      <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
        Mensaje al cliente (se enviará por email)
      </label>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder={
          isNew
            ? "Guarda la reparación primero..."
            : customerEmail
            ? `Escribe el mensaje para ${customerEmail}...`
            : "Este cliente no tiene email registrado."
        }
        disabled={isNew || !customerEmail}
        data-testid="customer-message-text"
        className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none disabled:bg-[#F7F7F4]"
      />

      {/* Lista de adjuntos previsualizada */}
      {attachments.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="customer-message-attachments">
          {attachments.map((a) => (
            <div key={a.id} className="relative border border-[#E5E5E0]">
              <img
                src={`data:${a.content_type};base64,${a.data_b64}`}
                alt={a.name}
                className="h-16 w-16 object-cover"
              />
              <button
                type="button"
                onClick={() => removeAttachment(a.id)}
                className="absolute -top-1 -right-1 bg-red-600 text-white p-0.5 hover:bg-red-700"
                title="Quitar"
                data-testid={`customer-message-remove-${a.id}`}
              >
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onPickFiles}
          className="hidden"
          data-testid="customer-message-file-input"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={isNew || !customerEmail}
          data-testid="customer-message-attach-btn"
          className="inline-flex items-center gap-1 ds-card hover:border-[#111110] text-[#111110] px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
        >
          <Plus size={12} /> Adjuntar foto
        </button>
        {totalBytes > 0 && (
          <span className="font-mono text-[10px] text-[#525252]">
            {(totalBytes / 1024 / 1024).toFixed(2)} / 10 MB
          </span>
        )}
        {totalBytes > MAX_TOTAL_ATTACH_BYTES && (
          <span className="font-mono text-[10px] text-red-700">
            Demasiado peso (máx 10 MB).
          </span>
        )}
        <button
          type="button"
          onClick={send}
          disabled={!canSend || sending}
          data-testid="customer-message-send"
          className="ml-auto inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-1.5 text-xs font-semibold disabled:opacity-50 transition-colors"
        >
          <Mail size={12} />
          {sending ? "Enviando..." : "Enviar al cliente"}
        </button>
      </div>
      {!customerEmail && !isNew && (
        <div className="text-[10px] text-amber-700 mt-2">
          Este cliente no tiene email en su ficha. Edita el cliente para añadirlo.
        </div>
      )}
    </div>
  );
}
